import React from 'react';
import { toast } from 'react-toastify';

import { activateBypassTrap } from './bypassTrapStore';

// Native events that signal a user interacting with the element. A genuinely disabled
// DOM node dispatches none of these, so a listener attached while `disabled` is true can
// only ever fire if the `disabled` attribute was stripped by hand (e.g. via the browser
// inspector). That is exactly the bypass we want to catch.
const TRAP_EVENTS = ['pointerdown', 'mousedown', 'click', 'keydown', 'focus', 'input'] as const;

/**
 * Generic trap for a control that is *supposed* to be disabled.
 *
 * Attach the returned ref to any input or button (or pass it as MUI `inputRef`). While
 * `disabled` is true the element is still rendered natively disabled as usual — but if a
 * tech-savvy user re-enables it in the inspector and interacts with it, this intercepts
 * that first interaction, swallows the default action so nothing adverse happens, forces
 * the element back to disabled in JS, and fires the bypass-trap effect instead.
 *
 * @param disabled Whether the element is currently supposed to be disabled. The trap is
 *   armed only while this is true, so it never fires during legitimate use.
 * @returns A callback ref to attach to the DOM node.
 */
export const useDisabledBypassTrap = (disabled: boolean): ((node: HTMLElement | null) => void) => {
  const [node, setNode] = React.useState<HTMLElement | null>(null);
  const setRef = React.useCallback((next: HTMLElement | null) => setNode(next), []);

  React.useEffect(() => {
    if (!node || !disabled) return;

    const handleBypass = (event: Event) => {
      // The trap is armed only while React considers this element disabled, but some
      // browsers still dispatch pointer events on a genuinely disabled control. So confirm
      // the bypass: if the live DOM still carries the `disabled` attribute the user hasn't
      // re-enabled it (this is a spurious event) — bail. Only once the attribute has been
      // stripped by hand (e.g. via the inspector) is this a real bypass.
      if (node.hasAttribute('disabled')) return;

      // Confirmed bypass: swallow the action, then re-lock the element in JS by putting the
      // `disabled` attribute right back (React wants it disabled anyway, so this just closes
      // the window the inspector edit opened). Drop focus and fire the effect.
      event.preventDefault();
      event.stopImmediatePropagation();
      node.setAttribute('disabled', '');
      if ('disabled' in node) (node as unknown as { disabled: boolean }).disabled = true;
      node.blur?.();
      if (activateBypassTrap()) {
        toast('Nice try! 🎉', { toastId: 'disabled-bypass-trap' });
      }
    };

    // Capture phase so we run before (and can cut off) React's delegated handlers.
    TRAP_EVENTS.forEach(type => node.addEventListener(type, handleBypass, true));
    return () => TRAP_EVENTS.forEach(type => node.removeEventListener(type, handleBypass, true));
  }, [node, disabled]);

  return setRef;
};
